// utils/finance.ts

// Simple cache for weekly payments
const weeklyPaymentCache = new Map<string, number>();

/**
 * Calculates the estimated weekly payment for a loan.
 * 
 * @param price The total price of the item.
 * @param annualRate The annual interest rate (e.g., 9.98 for 9.98%). Defaults to 9.98.
 * @param termYears The loan term in years. Defaults to 7.
 * @param down The down payment amount. Defaults to 500.
 * @returns The estimated weekly payment, rounded to the nearest whole number.
 */
export function calculateWeeklyPayment(
  price: number, 
  annualRate: number = 9.98, // TODO: Get from site main store config
  termYears: number = 7,      // TODO: Get from site main store config
  down: number = 500          // TODO: Get from site main store config
): number {
  // Ensure price is a valid number
  const validPrice = Number(price) || 0;
  if (validPrice <= 0) {
    return 0;
  }

  const principal = Math.max(0, validPrice - down);
  if (principal <= 0) {
    return 0; // No payment needed if down payment covers price
  }
  
  const cacheKey = `${principal}-${annualRate}-${termYears}`;
  
  if (weeklyPaymentCache.has(cacheKey)) {
    return weeklyPaymentCache.get(cacheKey)!;
  }

  // Handle zero interest rate case
  if (annualRate <= 0) {
    const totalWeeks = termYears * 52;
    const weekly = totalWeeks > 0 ? principal / totalWeeks : 0;
    const rounded = Math.round(weekly);
    weeklyPaymentCache.set(cacheKey, rounded);
    return rounded;
  }

  // Monthly calculation first
  const monthlyRate = annualRate / 100 / 12;
  const monthlyPayments = termYears * 12;
  
  // Standard loan payment formula
  const monthly = (principal * monthlyRate * Math.pow(1 + monthlyRate, monthlyPayments)) / 
                 (Math.pow(1 + monthlyRate, monthlyPayments) - 1);

  // Convert to weekly
  const weekly = (monthly * 12) / 52;
  const rounded = Math.round(weekly);
  
  // Cache the result before returning
  weeklyPaymentCache.set(cacheKey, rounded);
  return rounded; 
} 